import { CheckCircle2, XCircle, AlertTriangle, Globe2, Building2, ShieldCheck, MapPin, Briefcase, ArrowRight, Check, MessageSquare, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { renderInline } from "../lib/content";
import CheckReportDownload from "./CheckReportDownload";
import SpecialistEnquiry from "./SpecialistEnquiry";

function riskColors(label) {
  switch (label) {
    case "Critical": return { chip: "bnp-chip-danger", bar: "bg-red-500" };
    case "High": return { chip: "bnp-chip-warn", bar: "bg-amber-500" };
    case "Moderate": return { chip: "bnp-chip-warn", bar: "bg-amber-400" };
    case "Low":
    default: return { chip: "bnp-chip-teal", bar: "bg-[#71edc9]" };
  }
}

const INDUSTRY_SICS = {
  trades: ["43220", "43210", "41202", "43910", "16230"],
  hair_beauty: ["96020"],
  driving: ["85530"],
  hospitality: ["56102", "10711"],
  dental_health: ["86230", "86900"],
  fitness: ["93130", "85510"],
  accountancy: ["69201", "66220"],
  retail: ["47762", "47210", "14130", "18120"],
  marketing: ["73110"],
  childcare: ["88910"],
  removals_courier: ["49420", "53202"],
  cleaning: ["81210"],
  gardening: ["81300"],
  auto: ["45200"],
  funeral: ["96030"],
  security: ["80200"],
  education: ["85590"],
  other: [],
};

export default function ReportCard({ result, checkoutHref, query, industry, postcode }) {
  if (!result) return null;
  const c = riskColors(result.risk_label);
  const industrySics = (result.industry && INDUSTRY_SICS[result.industry]) || [];
  const hasContext = result.industry_label || result.postcode_area;

  // A "specialist first" CTA replaces the self-serve "Buy now" CTA when:
  //   1. The name is on NBR's protected register (any score), OR
  //   2. The computed risk_label is "Critical" - multiple direct conflicts found
  //      and the buyer needs consultation before incorporating / spending.
  const nbrConflict = result.nbr_available === false;
  const isCritical = result.risk_label === "Critical";
  const needsSpecialist = nbrConflict || isCritical;

  return (
    <div className="space-y-5" data-testid="report-card">
      {/* Risk header card with prominent CTA */}
      <div className="bnp-card p-6 md:p-8 bnp-fade-up">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <span className="bnp-chip bnp-chip-navy" data-testid="report-query-chip">Your name. {result.query}</span>
            <div className="mt-3 flex items-start justify-between gap-3 flex-wrap">
              <h3 className="font-display text-2xl md:text-3xl text-[#04273d] font-bold tracking-tight leading-tight" data-testid="report-headline">
                {needsSpecialist
                  ? "Let's review this one together."
                  : "Good news, this name looks suitable for protection"}
              </h3>
              {query && (
                <CheckReportDownload
                  query={query}
                  industry={industry}
                  postcode={postcode}
                  buttonClass="inline-flex items-center gap-1.5 text-xs font-display font-semibold text-[#04273d] bg-[#71edc9]/35 hover:bg-[#71edc9]/60 px-3 py-1.5 rounded-full transition-colors shrink-0"
                  buttonLabel="Download PDF"
                  testid="check-report-download"
                />
              )}
            </div>
            {needsSpecialist ? (
              <div className="mt-3 max-w-xl space-y-2" data-testid="report-headline-body-specialist">
                <p className="text-[#447694] text-sm md:text-base leading-relaxed">
                  We&rsquo;ve identified some similarities that warrant a closer look.
                </p>
                <p className="text-[#447694] text-sm md:text-base leading-relaxed">
                  The good news is that the right answer often depends on factors our automated checks can&rsquo;t see, such as how long you&rsquo;ve been trading, where you operate, and how your name is used.
                </p>
              </div>
            ) : (
              <p className="text-[#447694] text-sm md:text-base mt-3 max-w-xl leading-relaxed" data-testid="report-headline-body">
                Our checks found no significant conflicts that would normally prevent you from protecting this name.
              </p>
            )}

            <div className="mt-5 flex items-center gap-5">
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold text-[#04273d]" data-testid="report-risk-score">{result.risk_score}</span>
                  <span className="font-display text-sm text-[#447694]">/100</span>
                </div>
                <span className={`bnp-chip ${c.chip} mt-1`} data-testid="report-risk-label">{result.risk_label} risk</span>
              </div>
              <div className="flex-1 max-w-xs">
                <div className="flex items-center justify-between text-[10px] font-mono-brand tracking-wider uppercase text-[#447694] mb-1">
                  <span>Lower&nbsp;risk</span>
                  <span>Higher&nbsp;risk</span>
                </div>
                <div className="relative h-2.5 rounded-full bg-gradient-to-r from-emerald-400/40 via-amber-400/50 to-rose-500/60 overflow-hidden">
                  <div className="absolute top-0 bottom-0 w-[3px] rounded-full bg-[#04273d] shadow-md" style={{ left: `calc(${result.risk_score}% - 1.5px)` }} />
                </div>
                <p className="text-xs text-[#04273d] font-display font-semibold mt-2 leading-snug" data-testid="report-risk-direction-note">
                  The higher the score, the higher the risk to your business name.
                </p>
              </div>
            </div>
          </div>

          {/* Prominent inline CTA - swaps to "Speak to a specialist" when the name is
              on the NBR register OR when the computed risk is "Critical". The whole
              green panel gently pulses (same effect as the Check name button on the
              search bar) so the eye is drawn to it. */}
          {!needsSpecialist ? (
            <div
              className="bnp-pulse-panel lg:col-span-1 bnp-cta-band rounded-2xl p-6 flex flex-col justify-between relative hover:-translate-y-0.5 transition-transform"
              data-testid="report-cta-inline"
            >
              <div className="relative">
                <div className="inline-flex items-center justify-center h-11 w-11 rounded-2xl bg-[#04273d] text-[#71edc9] mb-3 shadow-md">
                  <ShieldCheck size={22} />
                </div>
                <p className="font-display font-bold text-[#04273d] text-xl leading-tight">Get protected now</p>
                <p className="text-[13px] text-[#04273d]/90 mt-2 leading-relaxed font-medium">
                  Create your record of use, access UK-based support, and benefit from up to <strong>£10,000</strong> of passing-off assistance.
                </p>
              </div>
              <Link
                to={checkoutHref || "/checkout/details?plan=annual"}
                className="relative bg-[#04273d] text-white hover:bg-[#0a3b59] active:scale-[0.98] transition-all px-6 py-4 rounded-full font-display font-bold text-base inline-flex items-center justify-center gap-2 mt-5"
                data-testid="report-get-protected-button"
              >
                Protect My Name <ArrowRight size={16} />
              </Link>
              <p className="text-[10.5px] text-[#04273d]/75 mt-2.5 text-center relative font-semibold tracking-wide uppercase">From £15 per month or £155.40 per annum including VAT</p>
            </div>
          ) : (
            <div className="lg:col-span-1 rounded-2xl p-5 flex flex-col justify-between relative bg-gradient-to-br from-amber-400 to-amber-500 text-[#04273d]" data-testid="report-cta-specialist">
              <div className="relative">
                <MessageSquare className="text-[#04273d]" size={26} />
                <p className="font-display font-bold text-[#04273d] mt-3 text-lg">Get a free review</p>
                <p className="text-xs text-[#04273d]/90 mt-2 leading-relaxed font-medium">
                  We&rsquo;ve picked up a few similarities between your name and other UK businesses.
                </p>
                <p className="text-xs text-[#04273d]/90 mt-1.5 leading-relaxed font-medium">
                  The best next step is usually a quick review with one of our specialists. They&rsquo;ll tell you whether protection still makes sense, or whether a small tweak to the name would help first.
                </p>
              </div>
              <SpecialistEnquiry
                businessName={result.query}
                matchedName={result.nbr_protected_match}
                buttonClass="relative bg-[#04273d] text-white hover:bg-[#0a3b59] transition-colors px-5 py-3 rounded-full font-display font-bold text-sm inline-flex items-center justify-center gap-2 mt-4"
                buttonLabel="Request Free Review"
                testid="report-specialist-enquiry"
              />
              <p className="text-[10px] text-[#04273d]/80 mt-2 text-center relative font-medium">No obligation. UK-based team.</p>
            </div>
          )}
        </div>

        {/* NBR Business Name Availability - tick if clean, amber warning if matched.
            Hidden entirely on Critical-risk reports (the prominent specialist CTA above
            covers the message and the green tick can read as conflicting). */}
        {!isCritical && (
        <div className={`mt-5 rounded-2xl border px-4 py-3 flex items-center justify-between gap-3 ${result.nbr_available !== false ? "bg-emerald-50 border-emerald-300/60" : "bg-amber-50 border-amber-300/60"}`} data-testid="report-nbr-row">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${result.nbr_available !== false ? "bg-emerald-600" : "bg-amber-500"}`}>
              {result.nbr_available !== false ? <Check size={18} className="text-white" /> : <AlertTriangle size={18} className="text-white" />}
            </div>
            <div className="min-w-0">
              <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">NBR Business Name Availability</p>
              <p className="font-display text-[15px] text-[#04273d] font-semibold truncate" data-testid="report-nbr-message">
                {result.nbr_available !== false
                  ? "Available to protect with NBR."
                  : "Potential conflict - please speak to a specialist."}
              </p>
            </div>
          </div>
          {result.nbr_available !== false ? null : (
            <span className="hidden sm:inline-flex items-center gap-1 text-xs font-display font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-full shrink-0">
              Specialist review recommended
            </span>
          )}
        </div>
        )}

        {hasContext && (
          <div className="mt-5 pt-5 border-t border-[#9ac7f7]/25 grid sm:grid-cols-2 gap-3" data-testid="report-context-strip">
            {result.industry_label && (
              <div className="flex items-center justify-between gap-3 bg-[#f7f7f7] border border-[#9ac7f7]/25 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <Briefcase size={14} className="text-[#04273d]" />
                  <div>
                    <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">Industry</p>
                    <p className="text-sm text-[#04273d] font-medium">{result.industry_label}</p>
                  </div>
                </div>
                {result.industry_match_count > 0 && (
                  <span className="bnp-chip bnp-chip-warn text-[10px]" data-testid="report-industry-match-count">
                    {result.industry_match_count} same-industry conflict{result.industry_match_count === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            )}
            {result.postcode_area && (
              <div className="flex items-center justify-between gap-3 bg-[#f7f7f7] border border-[#9ac7f7]/25 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <MapPin size={14} className="text-[#04273d]" />
                  <div>
                    <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">Postcode area</p>
                    <p className="text-sm text-[#04273d] font-medium">{result.postcode_area}</p>
                  </div>
                </div>
                {result.region_match_count > 0 ? (
                  <span className="bnp-chip bnp-chip-warn text-[10px]" data-testid="report-region-match-count">
                    {result.region_match_count} regional conflict{result.region_match_count === 1 ? "" : "s"}
                  </span>
                ) : (
                  <span className="bnp-chip bnp-chip-teal text-[10px]">No regional conflicts</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* RECOMMENDATIONS, Business Name Protection ALWAYS leads (server returns it first) */}
      <div className="bnp-card p-7 md:p-9 bg-[#f7f7f7]/40 border-[#71edc9]/40 bnp-fade-up" style={{ animationDelay: "60ms" }} data-testid="report-recommendations">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-12 w-12 rounded-2xl bg-[#71edc9]/40 flex items-center justify-center">
            <AlertTriangle size={22} className="text-[#04273d]" />
          </div>
          <div>
            <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">What to do next</p>
            <h3 className="font-display text-2xl md:text-3xl text-[#04273d] font-semibold tracking-tight">Your recommendations</h3>
          </div>
        </div>

        {/* Lead recommendation, visually elevated. The server always returns the
            Business Name Protection action as suggestions[0]. */}
        {result.suggestions[0] && (
          <div className="bnp-cta-band rounded-2xl p-5 md:p-6 mb-4 relative" data-testid="report-lead-recommendation">
            <div className="flex items-start gap-4 relative">
              <div className="h-10 w-10 rounded-full bg-[#04273d] text-[#71edc9] flex items-center justify-center font-display font-bold shrink-0">
                1
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono-brand text-[10px] text-[#04273d]/80 tracking-widest uppercase mb-1.5 font-bold">Your single most important action</p>
                <p className="text-[#04273d] leading-relaxed text-[15px]">{renderInline(result.suggestions[0])}</p>
              </div>
            </div>
          </div>
        )}

        {/* Supporting recommendations */}
        {result.suggestions.length > 1 && (
          <ul className="space-y-3.5">
            {result.suggestions.slice(1).map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-[15px] text-[#04273d] leading-relaxed" data-testid={`report-recommendation-${i + 1}`}>
                <span className="flex-shrink-0 h-7 w-7 rounded-full bg-[#04273d] text-[#71edc9] flex items-center justify-center font-display font-semibold text-sm">{i + 2}</span>
                <span className="pt-0.5">{renderInline(s)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-7 flex flex-wrap items-center gap-3">
          {!needsSpecialist ? (
            <Link to={checkoutHref || "/checkout/details?plan=annual"} className="btn-primary" data-testid="report-recommendations-cta">
              Protect My Name <ArrowRight size={14} />
            </Link>
          ) : (
            <SpecialistEnquiry
              businessName={result.query}
              matchedName={result.nbr_protected_match}
              buttonClass="btn-primary"
              buttonLabel="Request Free Review"
              testid="report-recommendations-enquiry"
            />
          )}
          <Link to="/business-name-protection" className="btn-secondary text-sm">What&rsquo;s included?</Link>
        </div>
      </div>

      {/* DOMAIN AVAILABILITY, first because we can help with this */}
      <div className="bnp-card p-6 md:p-7 bnp-fade-up" style={{ animationDelay: "120ms" }} data-testid="report-domains-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#04273d]/5 flex items-center justify-center">
              <Globe2 size={18} className="text-[#04273d]" />
            </div>
            <div>
              <p className="font-display font-semibold text-[#04273d]">Domain availability</p>
              <p className="text-xs text-[#447694] mt-0.5">{result.domains.filter(d => d.available).length} of {result.domains.length} available across major UK extensions</p>
            </div>
          </div>
          <span className="bnp-chip bnp-chip-teal text-[10px]">Priority action</span>
        </div>
        <ul className="grid sm:grid-cols-2 gap-2">
          {result.domains.map((d) => (
            <li key={d.domain} className="flex items-center justify-between gap-2 text-sm bg-[#f7f7f7]/70 border border-[#9ac7f7]/25 rounded-xl px-4 py-3" data-testid={`report-domain-${d.domain}`}>
              <span className="font-mono-brand text-[#04273d] text-[13px] min-w-0 truncate">{d.domain}</span>
              {d.available ? (
                <span className="flex items-center gap-1.5 text-emerald-700 font-medium text-xs shrink-0">
                  <CheckCircle2 size={14} /> Available
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-red-700 font-medium text-xs shrink-0">
                  <XCircle size={14} /> Taken
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* COMPANIES HOUSE MATCHES */}
      <div className="bnp-card p-6 md:p-7 bnp-fade-up" style={{ animationDelay: "180ms" }} data-testid="report-companies-house-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-[#04273d]/5 flex items-center justify-center">
            <Building2 size={18} className="text-[#04273d]" />
          </div>
          <div>
            <p className="font-display font-semibold text-[#04273d]">Companies House similarity</p>
            <p className="text-xs text-[#447694] mt-0.5">{result.companies_house.length} match{result.companies_house.length === 1 ? "" : "es"} on the UK register</p>
          </div>
        </div>

        {result.companies_house.length === 0 ? (
          <div className="text-sm text-[#447694] py-4 flex items-center gap-2" data-testid="report-no-ch-matches">
            <CheckCircle2 size={16} className="text-emerald-500" />
            No close matches on the UK register.
          </div>
        ) : (
          <ul className="divide-y divide-[#9ac7f7]/25">
            {result.companies_house.map((m) => {
              const sameIndustry = industrySics.includes(m.sic_code);
              const chProfileUrl = m.company_number
                ? `https://find-and-update.company-information.service.gov.uk/company/${m.company_number}`
                : null;
              return (
                <li key={m.company_number} className="py-3 flex items-center justify-between gap-3" data-testid={`report-ch-match-${m.company_number}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {chProfileUrl ? (
                        <a
                          href={chProfileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-[#04273d] hover:text-[#0d9b80] text-sm truncate inline-flex items-center gap-1 underline underline-offset-[3px] decoration-[#9ac7f7]/60 decoration-[1px] hover:decoration-[#0d9b80]"
                          data-testid={`report-ch-match-${m.company_number}-link`}
                          title="View on Companies House (opens in a new tab)"
                        >
                          {m.company_name}
                          <ExternalLink size={11} className="opacity-70 shrink-0" />
                        </a>
                      ) : (
                        <p className="font-medium text-[#04273d] text-sm truncate">{m.company_name}</p>
                      )}
                      {sameIndustry && (
                        <span className="bnp-chip bnp-chip-warn text-[10px] py-0.5 px-1.5" data-testid={`report-ch-same-industry-${m.company_number}`}>
                          <Briefcase size={10} /> Same industry
                        </span>
                      )}
                    </div>
                    <p className="font-mono-brand text-[11px] text-[#447694] mt-0.5">
                      #{m.company_number} · {m.company_status.toUpperCase()} · Incorporated {m.incorporation_date}
                    </p>
                  </div>
                  <div className="w-20 shrink-0">
                    <div className="font-display text-xs text-[#04273d] text-right font-semibold">{m.similarity}%</div>
                    <div className="w-full h-1.5 bg-[#9ac7f7]/30 rounded-full overflow-hidden mt-1">
                      <div className={`h-full ${m.similarity >= 80 ? "bg-red-500" : m.similarity >= 65 ? "bg-amber-400" : "bg-[#9ac7f7]"}`} style={{ width: `${m.similarity}%` }} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
